import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Text } from '@chakra-ui/react';
import { getStokezes, createStokez, updateStokez, toggleLockStokez } from '../actions/stokezActions';
import { getGames } from '../actions/gameActions';
import { getSuperStokezes } from '../actions/superStokezActions';
import { getAdmins } from '../actions/adminActions';
import { fetchCompanies } from '../actions/companyActions';
import CascadingDropdown from '../components/CascadingDropdown';
import GameSelection from './GameSelection';
import GenericTable from '../components/GenericTable';
import BalanceTransferModal from '../components/BalanceTransferModal';
import TableSearchBar from '../components/TableSearchBar';
import { Plus, ChevronRight, DollarSign, Loader2, X, Lock, Unlock, Edit } from 'lucide-react';
import { throwUnlessApiSuccess } from '../utils/apiResponse';
import { applyRecipientBalancePatch } from '../utils/applyRecipientBalancePatch';
import styles from '../styles/ManagementTable.module.css';
import { usePrivilege } from '../hooks/usePrivilege';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { useAppToast } from '../hooks/useAppToast';
import { findById, filterGamesByAssignedIds, idStr } from '../utils/entityIds';
import { getLoggedInUserRole } from '../utils/auth';

const EMPTY_STOKEZ = {
  username: '',
  password: '',
  super_stokez_id: '',
  admin_id: '',
  company_id: '',
  gameIds: [],
  commission_perc: 5,
  target_perc: 15,
  adminTargetNTP: '',
};

const StokezManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { showSuccess, showWarning, showApiError } = useAppToast();
  const { can } = usePrivilege();
  const { filterColumns, canShowField } = useFieldPrivilege();
  const { stokezes, loading, error } = useSelector(state => state.stokez);
  const games = useSelector(state => state.game.games);
  const superStokezes = useSelector(state => state.superStokez.superStokezes);
  const admins = useSelector(state => state.admin.admins);
  const companies = useSelector(state => state.company.companies);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cascadingDropdownValues, setCascadingDropdownValues] = useState({});
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState({});
  const [newStokez, setNewStokez] = useState({ ...EMPTY_STOKEZ });
  const [availableGames, setAvailableGames] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const isSuperAdmin = getLoggedInUserRole() === 'super_admin';

  const handleOpenTransferModal = (toRole, toId, recipient = {}) => {
    setTransferDetails({
      toRole,
      toId,
      recipientBalance: recipient.balance,
      recipientBalanceCheck: recipient.balance_check,
    });
    setIsTransferOpen(true);
  };

  const handleBalanceTransfer = async ({ fromRole, fromId, toRole, toId, amount }) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL_ADMIN}`, {
      apiName: 'user',
      command: 'transferBalance',
      fromRole,
      fromId,
      toRole,
      toId,
      amount,
    });
    return throwUnlessApiSuccess(response);
  };

  const location = useLocation();

  const filterSuperStokezId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const superStokezParam = params.get('super_stokez') || null;
    if (!superStokezParam) return null;
    const found = superStokezes.find((s) => s.username === superStokezParam);
    return found ? found._id : null;
  }, [location.search, superStokezes]);

  const [searchQuery, setSearchQuery] = useState('');

  const filteredBySuperStokez = useMemo(() => {
    if (!filterSuperStokezId) return stokezes;
    return stokezes.filter((s) => s.super_stokez_id === filterSuperStokezId);
  }, [stokezes, filterSuperStokezId]);

  const filteredStokezes = useMemo(() => {
    if (!searchQuery.trim()) return filteredBySuperStokez;
    const q = searchQuery.trim().toLowerCase();
    return filteredBySuperStokez.filter((s) => {
      const username = (s.username || '').toLowerCase();
      const superStokez = (s.super_stokez_name || '').toLowerCase();
      const admin = (s.admin_name || '').toLowerCase();
      const company = (s.company_name || '').toLowerCase();
      const gameCodes = (s.game_codes || []).join(' ').toLowerCase();
      return [username, superStokez, admin, company, gameCodes].some((str) => str.includes(q));
    });
  }, [filteredBySuperStokez, searchQuery]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const superStokezParam = queryParams.get('super_stokez');
    const adminParam = queryParams.get('admin');
    if (superStokezParam) {
      setNewStokez(prev => ({ ...prev, super_stokez_id: superStokezParam }));
    }
  }, [location]);


  useEffect(() => {
    console.log()
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getStokezes()),
          dispatch(getGames()),
          dispatch(getSuperStokezes()),
          dispatch(getAdmins()),
          dispatch(fetchCompanies())
        ]);
        console.log('Data fetched successfully');
      } catch (err) {
        console.error('Error fetching data:', err);
        showApiError(err, 'Error fetching data');
      }
    };
    fetchData();
  }, [dispatch, showApiError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStokez(prev => ({ ...prev, [name]: value }));
  };

  const handleCascadingDropdownChange = useCallback((selections) => {
    setNewStokez(prev => ({
      ...prev,
      ...selections,
      gameIds: [] // Reset gameIds when selections change
    }));
  }, []);

  useEffect(() => {
    if (!newStokez.super_stokez_id) {
      setAvailableGames([]);
      return;
    }
    const selectedSuperStokez = findById(superStokezes, newStokez.super_stokez_id);
    const filteredGames = filterGamesByAssignedIds(games, selectedSuperStokez?.gameIds);
    setAvailableGames(
      filteredGames.length
        ? filteredGames
        : games.length && newStokez.super_stokez_id
          ? games
          : []
    );
  }, [newStokez.super_stokez_id, superStokezes, games]);

  const handleGameSelection = useCallback((gameId) => {
    const id = idStr(gameId);
    setNewStokez(prev => {
      const prevGameIds = (prev.gameIds || []).map(idStr);
      return {
        ...prev,
        gameIds: prevGameIds.includes(id)
          ? prevGameIds.filter((gid) => gid !== id)
          : [...prevGameIds, id]
      };
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!newStokez.super_stokez_id || !newStokez.admin_id || !newStokez.company_id) {
        showWarning('Please select Super Stokez, Admin, and Company');
        return;
      }
      if (isEditing) {
        await dispatch(updateStokez(newStokez._id, newStokez));
        showSuccess('Stokez updated.');
      } else {
        await dispatch(createStokez(newStokez));
        showSuccess('Stokez created.');
      }
      setIsModalOpen(false);
      setNewStokez({ ...EMPTY_STOKEZ });
      setIsEditing(false);
      dispatch(getStokezes());
    } catch (error) {
      console.error('Failed to create/update stokez:', error);
      showApiError(error, `Failed to ${isEditing ? 'update' : 'create'} Stokez. Please try again.`);
    }
  };

  const handleEdit = (stokez) => {
    console.log('Editing Stokez:', stokez);
    setNewStokez({
      ...stokez,
      password: '',
      commission_perc: stokez.commission_perc ?? 5,
      target_perc: stokez.target_perc ?? 15,
      adminTargetNTP: stokez.adminTargetNTP ?? '',
    });
    setIsEditing(true);
    
    // Pre-select the cascading dropdown values
    setCascadingDropdownValues({
      company_id: stokez.company_id,
      admin_id: stokez.admin_id,
      super_stokez_id: stokez.super_stokez_id
    });
  
    // Set available games based on the selected Super Stokez
    const selectedSuperStokez = findById(superStokezes, stokez.super_stokez_id);
    const filteredGames = filterGamesByAssignedIds(games, selectedSuperStokez?.gameIds);
    setAvailableGames(filteredGames.length ? filteredGames : games);
  
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewStokez({ ...EMPTY_STOKEZ });
    setCascadingDropdownValues({});
    setIsEditing(false);
  };

  const handleToggleLock = async (stokezId, isLocked) => {
    try {
      await dispatch(toggleLockStokez(stokezId));
      dispatch(getStokezes());
      showSuccess(`Stokez ${isLocked ? 'unlocked' : 'locked'}.`);
    } catch (error) {
      showApiError(error, 'Failed to update lock status. Please try again.');
    }
  };

  const columns = useMemo(() => [
    {
      Header: 'Username',
      accessor: 'username',
      defaultWidth: 120,
    },
    {
      Header: 'Password',
      accessor: 'password',
      defaultWidth: 100,
    },
    {
      Header: 'Super Stokez',
      accessor: 'super_stokez_name',
      defaultWidth: 140,
    },
    {
      Header: 'Admin',
      accessor: 'admin_name',
      defaultWidth: 120,
    },
    {
      Header: 'Company',
      accessor: 'company_name',
      defaultWidth: 150,
    },
    {
      Header: 'Balance',
      accessor: 'balance',
      defaultWidth: 100,
      Cell: ({ row }) => <Text>{row.original.balance}</Text>,
    },
    {
      Header: 'Comm %',
      accessor: 'commission_perc',
      defaultWidth: 80,
      Cell: ({ value }) => value ?? '—',
    },
    {
      Header: 'Target %',
      accessor: 'target_perc',
      defaultWidth: 80,
      Cell: ({ value }) => value ?? '—',
    },
    {
      Header: 'Admin Target NTP',
      accessor: 'adminTargetNTP',
      defaultWidth: 120,
      Cell: ({ value }) => (value != null && value !== '' ? value : '—'),
    },
    {
      Header: 'Game Codes',
      accessor: 'game_codes',
      defaultWidth: 150,
      Cell: ({ value }) => value ? value.join(', ') : '',
    },
    {
      Header: 'Status',
      accessor: 'is_active',
      defaultWidth: 80,
      Cell: ({ value }) => (
        <Switch
          isChecked={value}
          isReadOnly
          colorScheme={value ? "green" : "red"}
        />
      )
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      defaultWidth: 200,
      Cell: ({ row }) => (
        <div className={styles.actionButtons}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              const base = slug ? `/${slug}/agent` : '/agent';
              const params = new URLSearchParams(location.search);
              params.set('stokez', row.original.username || row.original._id);
              navigate(`${base}?${params.toString()}`);
            }}
            title="Go to Agent"
            aria-label="Go to Agent"
          >
            <ChevronRight size={18} />
          </button>
          {can('stokez.edit') && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => handleEdit(row.original)}
            title="Edit Stokez"
            aria-label="Edit Stokez"
          >
            <Edit size={18} />
          </button>
          )}
          {can('stokez.lock') && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => handleToggleLock(row.original._id, row.original.isLocked)}
            title={row.original.isLocked ? 'Unlock' : 'Lock'}
            aria-label={row.original.isLocked ? 'Unlock' : 'Lock'}
          >
            {row.original.isLocked ? <Lock size={18} /> : <Unlock size={18} />}
          </button>
          )}
          {can('stokez.transfer') && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => handleOpenTransferModal('stokez', row.original.username, row.original)}
            title="Transfer Balance"
            aria-label="Transfer Balance"
          >
            <DollarSign size={18} />
          </button>
          )}
        </div>
      ),
    },
  ], [navigate, slug, location.search, can, handleToggleLock, handleEdit]);

  const visibleColumns = useMemo(
    () => filterColumns('stokez', columns),
    [columns, filterColumns]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }
  if (error && stokezes.length === 0) {
    return (
      <div className={styles.errorAlert}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.managementPage}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon} aria-hidden><Plus size={24} /></span>
          <h1 className={styles.title}>Stokez Management</h1>
        </div>
        {can('stokez.create') && (
        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            setIsEditing(false);
            setNewStokez({ ...EMPTY_STOKEZ });
            setIsModalOpen(true);
          }}
        >
          <Plus size={20} />
          <span>Add Stokez</span>
        </button>
        )}
      </div>

      <div className={styles.searchBarRow}>
        <TableSearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by username, super stokez, company..."
        />
      </div>

      <div className={styles.tableContainer}>
        <GenericTable columns={visibleColumns} data={filteredStokezes} showColumnFilters={false} />
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {isEditing ? 'Edit Stokez' : 'Add New Stokez'}
              </h2>
              <button type="button" className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.modalBody}>
                {canShowField('stokez', 'username', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Username <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={newStokez.username}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
                )}
                {canShowField('stokez', 'password', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Password {isEditing && <span className={styles.helpText}>(Leave blank to keep current password)</span>}
                    {!isEditing && <span className={styles.required}>*</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={newStokez.password}
                    onChange={handleInputChange}
                    className={styles.input}
                    required={!isEditing}
                  />
                </div>
                )}
                {(canShowField('stokez', 'super_stokez_id', 'form') ||
                  canShowField('stokez', 'admin_id', 'form') ||
                  canShowField('stokez', 'company_id', 'form')) && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Super Stokez / Admin / Company <span className={styles.required}>*</span>
                  </label>
                  <CascadingDropdown
                    onSelectionChange={handleCascadingDropdownChange}
                    initialValues={cascadingDropdownValues}
                    roleBeingCreated="stokez"
                  />
                </div>
                )}
                {canShowField('stokez', 'gameIds', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Games</label>
                    {availableGames.length > 0 ? (
                      <GameSelection
                        games={availableGames}
                        selectedGames={newStokez.gameIds}
                        onGameSelection={handleGameSelection}
                        isDisabled={!newStokez.super_stokez_id}
                      />
                    ) : (
                      <p className={styles.helpText}>No games available for the selected Super Stokez</p>
                    )}
                  </div>
                )}
                {(canShowField('stokez', 'commission_perc', 'form') ||
                  canShowField('stokez', 'target_perc', 'form') ||
                  (isSuperAdmin && canShowField('stokez', 'adminTargetNTP', 'form'))) && (
                  <>
                    <h3 className={styles.formSectionTitle}>Percentages</h3>
                    <div className={styles.formGrid}>
                      {canShowField('stokez', 'commission_perc', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Commission %</label>
                          <input
                            type="number"
                            name="commission_perc"
                            step="any"
                            value={newStokez.commission_perc}
                            onChange={handleInputChange}
                            className={styles.input}
                          />
                        </div>
                      )}
                      {canShowField('stokez', 'target_perc', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Target %</label>
                          <input
                            type="number"
                            name="target_perc"
                            step="any"
                            min={isSuperAdmin ? undefined : 0}
                            max={isSuperAdmin ? undefined : 100}
                            value={newStokez.target_perc}
                            onChange={handleInputChange}
                            className={styles.input}
                          />
                        </div>
                      )}
                      {isSuperAdmin && canShowField('stokez', 'adminTargetNTP', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Admin Target NTP</label>
                          <input
                            type="number"
                            name="adminTargetNTP"
                            step="any"
                            value={newStokez.adminTargetNTP}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="Leave empty for none"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className={styles.modalFooter}>
                <button type="submit" className={styles.submitButton}>
                  {isEditing ? 'Update Stokez' : 'Add Stokez'}
                </button>
                <button type="button" className={styles.cancelButton} onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <BalanceTransferModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        toRole={transferDetails.toRole}
        toId={transferDetails.toId}
        recipientBalance={transferDetails.recipientBalance}
        recipientBalanceCheck={transferDetails.recipientBalanceCheck}
        onTransfer={handleBalanceTransfer}
        onTransferSuccess={(r) => applyRecipientBalancePatch(dispatch, r)}
      />
    </div>
  );
};

export default StokezManagement;